import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Upload an image to Cloudinary via signed upload
 * @param {File} file - The file to upload
 * @param {string} folder - The folder to upload to (kittens, parents, gallery, uploads)
 * @returns {Promise<{url: string, public_id: string}>}
 */
export async function uploadToCloudinary(file, folder = 'uploads') {
  try {
    // Get signature from backend
    const sigResponse = await axios.get(`${API}/cloudinary/signature`, {
      params: { folder }
    });
    const sig = sigResponse.data;

    // Upload directly to Cloudinary
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);

    const uploadResponse = await axios.post(
      `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
      formData
    );

    return {
      url: uploadResponse.data.secure_url,
      public_id: uploadResponse.data.public_id
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
}

/**
 * Upload multiple images to Cloudinary
 * @param {File[]} files - Array of files to upload
 * @param {string} folder - The folder to upload to
 * @param {function} onProgress - Optional progress callback (index, total)
 * @returns {Promise<Array<{url: string, public_id: string}>>}
 */
export async function uploadMultipleToCloudinary(files, folder = 'uploads', onProgress) {
  const results = [];
  for (let i = 0; i < files.length; i++) {
    const result = await uploadToCloudinary(files[i], folder);
    results.push(result);
    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }
  return results;
}

/**
 * Delete an image from Cloudinary
 * @param {string} publicId - The public_id of the image to delete
 */
export async function deleteFromCloudinary(publicId) {
  try {
    await axios.delete(`${API}/cloudinary/delete`, {
      params: { public_id: publicId }
    });
  } catch (error) {
    console.error('Cloudinary delete error:', error);
    throw error;
  }
}

export default { uploadToCloudinary, uploadMultipleToCloudinary, deleteFromCloudinary };
